import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Sprout, Lock } from "lucide-react";
import { toast } from "sonner";
import { adminApi, auth } from "@/lib/adminApi";
import { ADMIN } from "@/constants/testIds/landing";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || "/admin";

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", form);
      auth.saveToken(data.token);
      toast.success("Eingeloggt – willkommen!");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Login fehlgeschlagen. Bitte erneut versuchen.";
      setError(typeof detail === "string" ? detail : "Login fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={ADMIN.loginPage}
      className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-4"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-[#6B8E23] text-white">
            <Sprout size={22} strokeWidth={2.2} />
          </span>
          <span className="font-[Outfit] font-bold text-2xl tracking-tight">
            Waskowi
          </span>
        </Link>

        <div className="bg-white rounded-3xl border border-[#EAEAEA] shadow-sm p-8">
          <div className="flex items-center gap-3 mb-1">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-[#E8EFE1] text-[#6B8E23]">
              <Lock size={18} />
            </span>
            <h1 className="font-[Outfit] text-2xl font-semibold">Admin-Login</h1>
          </div>
          <p className="text-sm text-[#666] mb-6">
            Zugang nur für die Administration der Testphase.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-medium mb-2">E-Mail</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid={ADMIN.loginEmailInput}
                placeholder="admin@meieiko.app"
                className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 w-full focus:ring-2 focus:ring-[#6B8E23] focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Passwort</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
                data-testid={ADMIN.loginPasswordInput}
                placeholder="••••••••"
                className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 w-full focus:ring-2 focus:ring-[#6B8E23] focus:border-transparent outline-none"
              />
            </div>

            {error && (
              <div
                data-testid={ADMIN.loginError}
                className="text-sm text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-2xl px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid={ADMIN.loginSubmit}
              className="w-full rounded-full px-6 py-3 font-semibold bg-[#1A1A1A] text-white hover:bg-[#6B8E23] transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Anmelden…" : "Anmelden"}
            </button>
          </form>
        </div>

        <p className="text-xs text-[#888] text-center mt-6">
          Zurück zur{" "}
          <Link to="/" className="underline underline-offset-2 hover:text-[#6B8E23]">
            Startseite
          </Link>
        </p>
      </div>
    </div>
  );
}
